// withAppStore.jsx - Final version
import React, { useState, useEffect } from 'react'
import AppStore from '../stores/AppStore'
import { useAuth } from './AuthContext'

/**
 * Higher-Order Component to connect components to AppStore data and authentication
 * This HOC should be used for components that need access to AppStore data and auth functions
 *
 * @param {React.Component} WrappedComponent - The component to wrap
 * @returns {React.Component} - The wrapped component with AppStore data and authentication
 */
export default function withAppStore (WrappedComponent) {
  return function WithAppStoreComponent (props) {
    // Make AppStore globally accessible for direct access
    if (typeof window !== 'undefined') {
      window.AppStore = AppStore
    }

    // Get initial state from AppStore matching its structure
    const [storeData, setStoreData] = useState({
      data: AppStore.data || {
        customer: { data: {}, ready: false },
        user: { data: {}, ready: false },
        notifications: { popUp: {} },
        deviceSettings: {},
      },
      pages: AppStore.pages || {},
      route: { path: window.location.pathname },
    })

    // Get authentication from AuthContext
    const auth = useAuth()

    // Listen for changes in AppStore
    useEffect(() => {
      // Handle store changes
      const handleStoreChange = () => {
        setStoreData({
          data: AppStore.data,
          pages: AppStore.pages,
          route: { path: window.location.pathname },
        })
      }

      // Add change listener when component mounts
      AppStore.addChangeListener(handleStoreChange)

      // Initial data load
      handleStoreChange()

      // Remove change listener when component unmounts
      return () => {
        AppStore.removeChangeListener(handleStoreChange)
      }
    }, [])

    // Extract commonly used values from AppStore data
    const level = storeData.data?.user?.permissions?.level || 0
    const tempConv = storeData.data?.user?.locale?.tempConv || 'f'

    // Combine AppStore data with authentication data and original props
    const combinedProps = {
      ...props,
      ...storeData,
      level,
      tempConv,
      auth, // Add auth from context, this will override storeData.auth
    }

    // Render the wrapped component with combined props
    return <WrappedComponent {...combinedProps} />
  }
}
